import { useEffect, useMemo, useState } from "react";
import type { Booking } from "@/mocks/admin/bookings";

type Props = {
  open: boolean;
  onClose: () => void;
  bookings: Booking[];
};

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// Daily crew capacity (assumption: 4 crews × 1 job/day = 4 jobs/day)
const DAILY_CAPACITY = 4;

// Match strings like "Sat, Jun 14" — parses into a Date in the current/next year
function parseBookingDate(str: string, referenceYear: number): Date | null {
  if (!str) return null;
  const match = str.match(/([A-Za-z]+),?\s+([A-Za-z]+)\s+(\d{1,2})/);
  if (!match) return null;
  const monthName = match[2];
  const day = parseInt(match[3], 10);
  const monthIdx = MONTHS.findIndex(
    (m) => m.toLowerCase().startsWith(monthName.toLowerCase()),
  );
  if (monthIdx < 0) return null;
  return new Date(referenceYear, monthIdx, day);
}

const stageColors: Record<string, { dot: string; bg: string; text: string }> = {
  new: { dot: "bg-amber-500", bg: "bg-amber-50", text: "text-amber-700" },
  quoted: { dot: "bg-indigo-500", bg: "bg-indigo-50", text: "text-indigo-700" },
  confirmed: { dot: "bg-brand-500", bg: "bg-brand-50", text: "text-brand-700" },
  scheduled: { dot: "bg-emerald-500", bg: "bg-emerald-50", text: "text-emerald-700" },
};

export default function AvailabilityModal({ open, onClose, bookings }: Props) {
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setCursor(new Date(today.getFullYear(), today.getMonth(), 1));
      setSelectedDay(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open) onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  // Group bookings by yyyy-mm-dd key
  const bookingsByDate = useMemo(() => {
    const map = new Map<string, Booking[]>();
    bookings.forEach((b) => {
      const d = parseBookingDate(b.preferredDate, cursor.getFullYear());
      if (!d) return;
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(b);
    });
    return map;
  }, [bookings, cursor]);

  // Build calendar grid
  const grid = useMemo(() => {
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells: { date: Date | null; key: string }[] = [];
    for (let i = 0; i < firstDay; i++) cells.push({ date: null, key: `pad-${i}` });
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month, d);
      cells.push({ date, key: `${year}-${month}-${d}` });
    }
    while (cells.length % 7 !== 0) cells.push({ date: null, key: `end-${cells.length}` });
    return cells;
  }, [cursor]);

  const totalConfirmedThisMonth = useMemo(() => {
    let count = 0;
    bookingsByDate.forEach((arr) => {
      count += arr.filter((b) => b.stage === "confirmed" || b.stage === "scheduled").length;
    });
    return count;
  }, [bookingsByDate]);

  const busiestDay = useMemo(() => {
    let max = 0;
    let dayKey = "";
    bookingsByDate.forEach((arr, key) => {
      if (arr.length > max) {
        max = arr.length;
        dayKey = key;
      }
    });
    if (!dayKey) return null;
    const [y, m, d] = dayKey.split("-").map(Number);
    return { date: new Date(y, m, d), count: max };
  }, [bookingsByDate]);

  if (!open) return null;

  const selectedItems = selectedDay ? bookingsByDate.get(selectedDay) || [] : [];

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-ink-900/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div
          className="bg-white w-full max-w-6xl rounded-lg border border-ink-100 max-h-[92vh] flex flex-col pointer-events-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-ink-100 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 flex items-center justify-center rounded-md bg-emerald-100 text-emerald-700">
                <i className="ri-calendar-check-line text-lg"></i>
              </span>
              <div>
                <h2 className="font-display text-lg font-bold text-ink-900">
                  Crew & Booking Availability
                </h2>
                <div className="text-xs text-ink-500">
                  See which days are open, busy, or fully booked before confirming new moves
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-cream-100 text-ink-500 cursor-pointer"
              aria-label="Close"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>

          {/* Stats strip */}
          <div className="px-6 py-4 border-b border-ink-100 bg-cream-50 grid grid-cols-2 md:grid-cols-4 gap-3 shrink-0">
            <StatTile
              icon="ri-calendar-2-line"
              iconClass="text-indigo-700 bg-indigo-50"
              label="Daily Capacity"
              value={`${DAILY_CAPACITY} jobs`}
              sub="4 active crews"
            />
            <StatTile
              icon="ri-check-double-line"
              iconClass="text-emerald-700 bg-emerald-50"
              label="Confirmed This Month"
              value={String(totalConfirmedThisMonth)}
              sub="locked in"
            />
            <StatTile
              icon="ri-fire-line"
              iconClass="text-rose-700 bg-rose-50"
              label="Busiest Day"
              value={
                busiestDay
                  ? `${MONTHS[busiestDay.date.getMonth()].slice(0, 3)} ${busiestDay.date.getDate()}`
                  : "—"
              }
              sub={busiestDay ? `${busiestDay.count} jobs` : "no data"}
            />
            <StatTile
              icon="ri-leaf-line"
              iconClass="text-brand-700 bg-brand-50"
              label="Open Slots"
              value={String(
                Math.max(
                  0,
                  grid.filter((c) => c.date).length * DAILY_CAPACITY -
                    Array.from(bookingsByDate.values()).reduce((s, a) => s + a.length, 0),
                ),
              )}
              sub="this month"
            />
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto scrollbar-thin">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
              {/* Calendar */}
              <div className="lg:col-span-2 p-6 border-b lg:border-b-0 lg:border-r border-ink-100">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-display text-lg font-bold text-ink-900">
                    {MONTHS[cursor.getMonth()]} {cursor.getFullYear()}
                  </h3>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() =>
                        setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))
                      }
                      className="w-9 h-9 flex items-center justify-center rounded-md hover:bg-cream-100 text-ink-500 cursor-pointer"
                      aria-label="Previous month"
                    >
                      <i className="ri-arrow-left-s-line"></i>
                    </button>
                    <button
                      onClick={() =>
                        setCursor(new Date(today.getFullYear(), today.getMonth(), 1))
                      }
                      className="px-3 h-9 flex items-center rounded-md hover:bg-cream-100 text-ink-700 text-xs font-semibold cursor-pointer whitespace-nowrap"
                    >
                      Today
                    </button>
                    <button
                      onClick={() =>
                        setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))
                      }
                      className="w-9 h-9 flex items-center justify-center rounded-md hover:bg-cream-100 text-ink-500 cursor-pointer"
                      aria-label="Next month"
                    >
                      <i className="ri-arrow-right-s-line"></i>
                    </button>
                  </div>
                </div>

                {/* Weekday header */}
                <div className="grid grid-cols-7 gap-1.5 mb-1.5">
                  {WEEKDAYS.map((d) => (
                    <div
                      key={d}
                      className="text-[10px] font-bold uppercase tracking-wider text-ink-500 text-center py-1"
                    >
                      {d}
                    </div>
                  ))}
                </div>

                {/* Grid */}
                <div className="grid grid-cols-7 gap-1.5">
                  {grid.map((cell) => {
                    if (!cell.date) {
                      return <div key={cell.key} className="aspect-square"></div>;
                    }
                    const items = bookingsByDate.get(cell.key) || [];
                    const count = items.length;
                    const isToday =
                      cell.date.toDateString() === today.toDateString();
                    const isSelected = selectedDay === cell.key;
                    const isPast =
                      cell.date < new Date(today.getFullYear(), today.getMonth(), today.getDate());

                    let load: "open" | "moderate" | "busy" | "full" = "open";
                    if (count >= DAILY_CAPACITY) load = "full";
                    else if (count >= 3) load = "busy";
                    else if (count >= 1) load = "moderate";

                    const loadStyles = {
                      open: "bg-white border-ink-100 hover:border-emerald-300",
                      moderate: "bg-amber-50/60 border-amber-200 hover:border-amber-400",
                      busy: "bg-rose-50/60 border-rose-200 hover:border-rose-400",
                      full: "bg-ink-900 border-ink-900 text-white",
                    }[load];

                    return (
                      <button
                        key={cell.key}
                        onClick={() => setSelectedDay(cell.key)}
                        className={`aspect-square rounded-md border p-1.5 flex flex-col items-start justify-between text-left cursor-pointer transition-all ${loadStyles} ${
                          isSelected
                            ? "ring-2 ring-ink-900 ring-offset-1"
                            : ""
                        } ${isPast && load === "open" ? "opacity-50" : ""}`}
                      >
                        <div className="flex items-center justify-between w-full">
                          <span
                            className={`text-xs font-bold ${
                              load === "full"
                                ? "text-white"
                                : isToday
                                ? "text-brand-600"
                                : "text-ink-900"
                            }`}
                          >
                            {cell.date.getDate()}
                          </span>
                          {isToday && (
                            <span className="text-[8px] font-bold uppercase tracking-wider text-brand-600">
                              Today
                            </span>
                          )}
                        </div>

                        {count > 0 ? (
                          <div className="w-full flex items-end justify-between">
                            <div className="flex -space-x-0.5">
                              {items.slice(0, 3).map((b) => (
                                <span
                                  key={b.id}
                                  className={`w-1.5 h-1.5 rounded-full ${
                                    stageColors[b.stage]?.dot || "bg-ink-400"
                                  } ${load === "full" ? "ring-1 ring-ink-900" : ""}`}
                                ></span>
                              ))}
                            </div>
                            <span
                              className={`text-[10px] font-bold tabular-nums ${
                                load === "full" ? "text-white" : "text-ink-700"
                              }`}
                            >
                              {count}/{DAILY_CAPACITY}
                            </span>
                          </div>
                        ) : (
                          <span className="text-[9px] font-semibold text-emerald-700 uppercase tracking-wider">
                            Open
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Legend */}
                <div className="mt-5 pt-4 border-t border-ink-100 flex flex-wrap items-center gap-4 text-[11px] text-ink-500">
                  <span className="font-bold uppercase tracking-wider text-ink-700">Legend</span>
                  <div className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded bg-white border border-ink-100"></span>
                    Open
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded bg-amber-50 border border-amber-200"></span>
                    Moderate (1–2)
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded bg-rose-50 border border-rose-200"></span>
                    Busy (3)
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded bg-ink-900"></span>
                    Fully booked
                  </div>
                </div>
              </div>

              {/* Day detail */}
              <div className="p-6 bg-cream-50/60">
                {selectedDay ? (
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-ink-500">
                          Selected day
                        </div>
                        <h3 className="font-display text-base font-bold text-ink-900 mt-0.5">
                          {(() => {
                            const [y, m, d] = selectedDay.split("-").map(Number);
                            const dt = new Date(y, m, d);
                            return dt.toLocaleDateString("en-US", {
                              weekday: "long",
                              month: "long",
                              day: "numeric",
                            });
                          })()}
                        </h3>
                      </div>
                      <button
                        onClick={() => setSelectedDay(null)}
                        className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-white text-ink-500 cursor-pointer"
                      >
                        <i className="ri-close-line text-sm"></i>
                      </button>
                    </div>

                    <div className="bg-white border border-ink-100 rounded-md p-3 mb-4 flex items-center justify-between">
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-ink-500">
                          Capacity
                        </div>
                        <div className="text-sm font-bold text-ink-900 mt-0.5">
                          {selectedItems.length} / {DAILY_CAPACITY} jobs booked
                        </div>
                      </div>
                      <div className="w-24 h-2 bg-cream-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${
                            selectedItems.length >= DAILY_CAPACITY
                              ? "bg-rose-500"
                              : selectedItems.length >= 3
                              ? "bg-amber-500"
                              : "bg-emerald-500"
                          }`}
                          style={{
                            width: `${Math.min(
                              100,
                              (selectedItems.length / DAILY_CAPACITY) * 100,
                            )}%`,
                          }}
                        ></div>
                      </div>
                    </div>

                    {selectedItems.length === 0 ? (
                      <div className="text-center py-10">
                        <span className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-emerald-50 text-emerald-600 mb-3">
                          <i className="ri-leaf-line text-xl"></i>
                        </span>
                        <div className="font-display text-sm font-bold text-ink-900">
                          Wide open
                        </div>
                        <div className="text-xs text-ink-500 mt-1">
                          No bookings on this day — perfect time to slot in a new move.
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-ink-500 mb-1">
                          Bookings ({selectedItems.length})
                        </div>
                        {selectedItems.map((b) => {
                          const sc = stageColors[b.stage];
                          return (
                            <div
                              key={b.id}
                              className="bg-white border border-ink-100 rounded-md p-3"
                            >
                              <div className="flex items-start gap-3">
                                <span
                                  className={`w-9 h-9 shrink-0 flex items-center justify-center rounded-full text-white text-xs font-bold ${b.color}`}
                                >
                                  {b.initials}
                                </span>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between gap-2">
                                    <div className="text-sm font-bold text-ink-900 truncate">
                                      {b.customer}
                                    </div>
                                    <span className="text-[10px] font-mono text-ink-500 shrink-0">
                                      {b.id}
                                    </span>
                                  </div>
                                  <div className="text-[11px] text-ink-500 truncate mt-0.5">
                                    {b.moveType}
                                  </div>
                                  <div className="flex items-center gap-2 mt-1.5 text-[10px]">
                                    <span className="flex items-center gap-1 text-ink-500">
                                      <i className="ri-time-line"></i>
                                      {b.preferredTime}
                                    </span>
                                    <span className="text-ink-300">•</span>
                                    <span className="flex items-center gap-1 text-ink-500 truncate">
                                      <i className="ri-route-line"></i>
                                      <span className="truncate">
                                        {b.fromCity.split(",")[0]} →{" "}
                                        {b.toCity.split(",")[0]}
                                      </span>
                                    </span>
                                  </div>
                                  <div className="mt-2 flex items-center justify-between">
                                    <span
                                      className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${sc?.bg} ${sc?.text} inline-flex items-center gap-1`}
                                    >
                                      <span
                                        className={`w-1.5 h-1.5 rounded-full ${sc?.dot}`}
                                      ></span>
                                      {b.stage.charAt(0).toUpperCase() + b.stage.slice(1)}
                                    </span>
                                    {b.estimatedQuote && (
                                      <span className="text-[11px] font-bold text-ink-900 tabular-nums">
                                        ${b.estimatedQuote.toLocaleString()}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="h-full flex flex-col items-center justify-center text-center py-16">
                    <span className="w-14 h-14 flex items-center justify-center rounded-full bg-white border border-ink-100 text-ink-400 mb-3">
                      <i className="ri-cursor-line text-xl"></i>
                    </span>
                    <div className="font-display text-sm font-bold text-ink-900">
                      Pick a day
                    </div>
                    <div className="text-xs text-ink-500 mt-1 max-w-[220px]">
                      Click any date in the calendar to see who's booked and how much capacity is
                      left.
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-ink-100 bg-white flex items-center justify-between gap-2 shrink-0 rounded-b-lg">
            <div className="text-[11px] text-ink-500">
              Capacity assumes <span className="font-bold text-ink-900">{DAILY_CAPACITY} active crews</span>{" "}
              available per day. Adjust in Settings → Operations.
            </div>
            <button
              onClick={onClose}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-semibold bg-ink-900 hover:bg-ink-800 text-white cursor-pointer whitespace-nowrap"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function StatTile({
  icon,
  iconClass,
  label,
  value,
  sub,
}: {
  icon: string;
  iconClass: string;
  label: string;
  value: string;
  sub: string;
}) {
  return (
    <div className="bg-white border border-ink-100 rounded-md px-3 py-2.5 flex items-center gap-3">
      <span
        className={`w-9 h-9 flex items-center justify-center rounded-md ${iconClass}`}
      >
        <i className={`${icon} text-base`}></i>
      </span>
      <div className="min-w-0">
        <div className="text-[10px] font-bold uppercase tracking-wider text-ink-500 truncate">
          {label}
        </div>
        <div className="font-display text-base font-bold text-ink-900 leading-tight">
          {value}
        </div>
        <div className="text-[10px] text-ink-500 truncate">{sub}</div>
      </div>
    </div>
  );
}